import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'
import {
  partitionBudgetCodes,
  type BudgetCode,
  type NewExternalBudgetCode,
  type NewOwnedBudgetCode,
} from '@budgetmonkey/shared'
import { useCurrentUser } from '@/auth/AuthProvider'
import { useResolvedFiscalYear } from '@/lib/fiscalYear'
import { queryKeys } from '@/lib/queryClient'
import {
  copyBudgetCodesToYear,
  countTransactionsForCode,
  createExternalBudgetCode,
  createOwnedBudgetCode,
  createBudgetCodesBatch,
  fetchAllBudgetCodes,
  fetchBudgetCodes,
  removeBudgetCode,
  setBudgetCodeActive,
  updateBudgetCode,
  updateExternalBudgetCode,
  applyBudgetImport,
  type BalanceUpdate,
  type BudgetCodeUpdate,
} from './api'
import { withDeadline } from '@/lib/withTimeout'

/**
 * All budget codes for the current user and fiscal year. Everything else in
 * this module derives from this one cached query — see the note in api.ts on
 * why it isn't split up.
 */
export function useBudgetCodes(explicitYear?: number) {
  const fiscalYear = useResolvedFiscalYear(explicitYear)
  const user = useCurrentUser()
  return useQuery({
    queryKey: queryKeys.budgetCodes(user.uid, fiscalYear),
    queryFn: () => withDeadline(fetchBudgetCodes(user.uid, fiscalYear)),
  })
}

/**
 * Every code the user has, in any year — for *naming* a charge's code.
 *
 * Separate from `useBudgetCodes` on purpose. That one is scoped to a year and
 * is what pickers must use, because a new charge belongs to this year's code.
 * This one resolves an id that may point at another year's document, which is
 * exactly what a charge moved across the July rollover does.
 */
export function useAllBudgetCodes() {
  const user = useCurrentUser()
  return useQuery({
    queryKey: ['budgetCodes', user.uid, 'all'],
    queryFn: () => withDeadline(fetchAllBudgetCodes(user.uid)),
  })
}

/** Codes by document id, across every year. The map screens should display from. */
export function useCodesById(): ReadonlyMap<string, BudgetCode> {
  const { data: codes = [] } = useAllBudgetCodes()
  return useMemo(
    () => new Map<string, BudgetCode>(codes.map((c) => [c.id ?? '', c])),
    [codes],
  )
}

export function useCopyBudgetCodesToYear() {
  const user = useCurrentUser()
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ fromYear, toYear }: { fromYear: number; toYear: number }) =>
      copyBudgetCodesToYear(user.uid, fromYear, toYear),
    // Both years' lists change, and so does the across-years map.
    onSuccess: () =>
      client.invalidateQueries({ queryKey: ['budgetCodes', user.uid] }),
  })
}

/** Active owned codes — the dashboard's set. Excludes external (§7.4). */
export function useOwnedBudgetCodes(explicitYear?: number) {
  const fiscalYear = useResolvedFiscalYear(explicitYear)
  const query = useBudgetCodes(fiscalYear)
  const data = useMemo(
    () => partitionBudgetCodes(query.data ?? []).owned,
    [query.data],
  )
  return { ...query, data }
}

/** Active external codes, for the picker and §8.2b. */
export function useExternalBudgetCodes(explicitYear?: number) {
  const fiscalYear = useResolvedFiscalYear(explicitYear)
  const query = useBudgetCodes(fiscalYear)
  const data = useMemo(
    () => partitionBudgetCodes(query.data ?? []).external,
    [query.data],
  )
  return { ...query, data }
}

export function useBudgetCode(codeId: string | undefined, explicitYear?: number) {
  const fiscalYear = useResolvedFiscalYear(explicitYear)
  const query = useBudgetCodes(fiscalYear)
  const data = useMemo<BudgetCode | undefined>(
    () => query.data?.find((c) => c.id === codeId),
    [query.data, codeId],
  )
  return { ...query, data }
}

/** Invalidate the one query every budget-code view reads from. */
function useInvalidate(fiscalYear: number) {
  const user = useCurrentUser()
  const client = useQueryClient()
  return () =>
    client.invalidateQueries({
      queryKey: queryKeys.budgetCodes(user.uid, fiscalYear),
    })
}

export function useCreateOwnedBudgetCode(explicitYear?: number) {
  const fiscalYear = useResolvedFiscalYear(explicitYear)
  const user = useCurrentUser()
  const invalidate = useInvalidate(fiscalYear)
  return useMutation({
    mutationFn: (input: NewOwnedBudgetCode) =>
      createOwnedBudgetCode(user.uid, fiscalYear, input),
    onSuccess: invalidate,
  })
}

export function useCreateExternalBudgetCode(explicitYear?: number) {
  const fiscalYear = useResolvedFiscalYear(explicitYear)
  const user = useCurrentUser()
  const invalidate = useInvalidate(fiscalYear)
  return useMutation({
    mutationFn: (input: NewExternalBudgetCode) =>
      createExternalBudgetCode(user.uid, fiscalYear, input),
    onSuccess: invalidate,
  })
}

export function useUpdateBudgetCode(explicitYear?: number) {
  const fiscalYear = useResolvedFiscalYear(explicitYear)
  const invalidate = useInvalidate(fiscalYear)
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: BudgetCodeUpdate }) =>
      updateBudgetCode(id, patch),
    onSuccess: invalidate,
  })
}

export function useUpdateExternalBudgetCode(explicitYear?: number) {
  const fiscalYear = useResolvedFiscalYear(explicitYear)
  const invalidate = useInvalidate(fiscalYear)
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: NewExternalBudgetCode }) =>
      updateExternalBudgetCode(id, input),
    onSuccess: invalidate,
  })
}

/**
 * How many transactions reference a code — what decides whether removing it
 * deletes or merely archives it, and what the button has to say before it's
 * pressed.
 *
 * Its own query rather than a filter over the cached transaction list: that
 * list is one fiscal year, and a reference from any year has to count here.
 */
export function useBudgetCodeReferenceCount(codeId: string | undefined) {
  const user = useCurrentUser()
  return useQuery({
    queryKey: queryKeys.budgetCodeReferences(user.uid, codeId ?? ''),
    queryFn: () => countTransactionsForCode(user.uid, codeId!),
    enabled: codeId !== undefined,
  })
}

export function useRemoveBudgetCode(explicitYear?: number) {
  const fiscalYear = useResolvedFiscalYear(explicitYear)
  const user = useCurrentUser()
  const client = useQueryClient()
  const invalidate = useInvalidate(fiscalYear)
  return useMutation({
    mutationFn: (id: string) => removeBudgetCode(user.uid, id),
    onSuccess: async (_result, id) => {
      await client.invalidateQueries({
        queryKey: queryKeys.budgetCodeReferences(user.uid, id),
      })
      await invalidate()
    },
  })
}

export function useSetBudgetCodeActive(explicitYear?: number) {
  const fiscalYear = useResolvedFiscalYear(explicitYear)
  const invalidate = useInvalidate(fiscalYear)
  return useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      setBudgetCodeActive(id, active),
    onSuccess: invalidate,
  })
}

export function useCreateBudgetCodesBatch(explicitYear?: number) {
  const fiscalYear = useResolvedFiscalYear(explicitYear)
  const user = useCurrentUser()
  const invalidate = useInvalidate(fiscalYear)
  return useMutation({
    mutationFn: (inputs: readonly NewOwnedBudgetCode[]) =>
      createBudgetCodesBatch(user.uid, fiscalYear, inputs),
    onSuccess: invalidate,
  })
}

/** Write a budget spreadsheet import into the given fiscal year. */
export function useApplyBudgetImport(explicitYear?: number) {
  const fiscalYear = useResolvedFiscalYear(explicitYear)
  const user = useCurrentUser()
  const invalidate = useInvalidate(fiscalYear)
  return useMutation({
    mutationFn: ({
      creates,
      updates,
    }: {
      creates: readonly NewOwnedBudgetCode[]
      updates: readonly BalanceUpdate[]
    }) => applyBudgetImport(user.uid, fiscalYear, creates, updates),
    onSuccess: invalidate,
  })
}
